/**
 * PreventionPathSection — iter979 · Phase 2
 *
 * Патиентски интерфейс за секцията "Твоят път към превенция" в BHI.
 * Показва всички модифицируеми рискови фактори (от `bhi_risk_factors`
 * колекцията) като карти с икони, групирани по категория. Пациентът
 * избира ниво чрез dropdown; клинични доказателства се показват
 * в info модал.
 *
 * Endpoints:
 *   GET  /api/bhi/risk-factors            → factors grouped by category
 *   GET  /api/bhi/risk-factors/my-selection
 *   POST /api/bhi/risk-factors/selection
 *   GET  /api/bhi/risk-factors/evidence/:id
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Info, Check, Save, RotateCcw, ChevronDown, ChevronUp,
  Wine, Cigarette, Pill, Baby, Factory, Scale, HeartPulse, Syringe,
  Sparkles, ShieldAlert, FlaskConical, BookOpen, Wand2,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import DerivedAnthropometricsStrip from "./DerivedAnthropometricsStrip";

// Map "icon" strings coming from the YAML `ui.icon` field → lucide-react components.
const ICON_MAP = {
  Wine, Cigarette, Pill, Baby, Factory, Scale, HeartPulse, Syringe,
  Sparkles, ShieldAlert, FlaskConical,
};

const CATEGORY_ACCENT = {
  lifestyle: "text-rose-500 bg-rose-500/10 border-rose-500/30",
  anthropometric: "text-sky-500 bg-sky-500/10 border-sky-500/30",
  reproductive: "text-violet-500 bg-violet-500/10 border-violet-500/30",
  medical_history: "text-amber-500 bg-amber-500/10 border-amber-500/30",
  medication: "text-indigo-500 bg-indigo-500/10 border-indigo-500/30",
  endocrine_disorder: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30",
  environmental: "text-orange-500 bg-orange-500/10 border-orange-500/30",
  benign: "text-teal-500 bg-teal-500/10 border-teal-500/30",
  family_history: "text-fuchsia-500 bg-fuchsia-500/10 border-fuchsia-500/30",
  pregnancy_related_endocrine_disorder:
    "text-pink-500 bg-pink-500/10 border-pink-500/30",
};

function accentFor(cat) {
  return CATEGORY_ACCENT[cat] || "text-slate-500 bg-slate-500/10 border-slate-500/30";
}

export default function PreventionPathSection() {
  const { API } = useAuth();
  const { isDark } = useTheme();

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [autoCount, setAutoCount] = useState(0);
  const [derived, setDerived] = useState(null);
  const [selections, setSelections] = useState({});   // { factor_id: level_key }
  const [initialSelections, setInitialSelections] = useState({});
  const [saving, setSaving] = useState(false);
  const [openCats, setOpenCats] = useState({});       // { category: bool }
  const [evidenceFor, setEvidenceFor] = useState(null); // factor summary
  const [evidence, setEvidence] = useState(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cat }, { data: sel }] = await Promise.all([
        axios.get(`${API}/bhi/risk-factors`),
        axios.get(`${API}/bhi/risk-factors/my-selection`),
      ]);
      setGroups(cat.groups || []);
      setTotal(cat.total || 0);
      setAutoCount(cat.auto_populated_count || 0);
      setDerived(cat.derived_anthropometrics || null);
      const initial = sel.selections || {};
      setSelections(initial);
      setInitialSelections(initial);
      // Open first category by default; keep others collapsed for mobile.
      const firstCat = (cat.groups || [])[0]?.category;
      setOpenCats(firstCat ? { [firstCat]: true } : {});
    } catch {
      toast.error("Грешка при зареждане на рисковите фактори.");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const setLevel = (fid, key) => {
    setSelections((s) => {
      const next = { ...s };
      if (!key) delete next[fid];
      else next[fid] = key;
      return next;
    });
  };

  const dirty = useMemo(() => {
    const ks = new Set([...Object.keys(selections), ...Object.keys(initialSelections)]);
    for (const k of ks) if (selections[k] !== initialSelections[k]) return true;
    return false;
  }, [selections, initialSelections]);

  const save = async () => {
    setSaving(true);
    // Snapshot current selection so we can roll back on failure.
    const attempted = { ...selections };
    try {
      const { data } = await axios.post(`${API}/bhi/risk-factors/selection`, { selections: attempted });
      setInitialSelections(attempted);
      toast.success(`Запазени ${data.selected_count} избора`);
      window.dispatchEvent(new CustomEvent("prevention-path-saved"));
    } catch (err) {
      // Roll back local state to the last-known-good server snapshot so the
      // "Запазено" badge and filled-counter stop showing false confidence.
      setSelections({ ...initialSelections });
      const detail = err?.response?.data?.detail || "Грешка при запазване.";
      toast.error(typeof detail === "string" ? detail : "Грешка при запазване.");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setSelections({ ...initialSelections });

  const openEvidence = async (factor) => {
    setEvidenceFor(factor);
    setEvidence(null);
    setEvidenceLoading(true);
    try {
      const { data } = await axios.get(`${API}/bhi/risk-factors/evidence/${factor.id}`);
      setEvidence(data);
    } catch {
      toast.error("Грешка при зареждане на доказателствата.");
    } finally {
      setEvidenceLoading(false);
    }
  };

  const cardCls = isDark
    ? "bg-slate-800/60 border-slate-700"
    : "bg-white border-slate-200";
  const chipCls = isDark ? "bg-slate-700/60 text-slate-100" : "bg-slate-100 text-slate-700";
  const mutedCls = isDark ? "text-slate-400" : "text-slate-500";
  const filledCount = useMemo(() => {
    let n = 0;
    for (const g of groups) {
      for (const f of g.factors) {
        if (selections[f.id] || f.auto_populated) n += 1;
      }
    }
    return n;
  }, [groups, selections]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center" data-testid="prev-path-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Зареждане на рисковите фактори…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="prevention-path-section">
      {/* iter1001 — Read-only derived anthropometrics tiles */}
      <DerivedAnthropometricsStrip derived={derived} isDark={isDark} />
      {/* Intro + progress */}
      <div className={`${cardCls} border rounded-xl p-4`} data-testid="prev-path-intro">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/15 text-emerald-500 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm md:text-base leading-tight">
              Избери твоето актуално ниво за всеки фактор, върху който можеш да въздействаш.
            </h4>
            <p className={`text-xs md:text-sm mt-1 leading-relaxed ${mutedCls}`}>
              Всеки фактор активира биологични процеси в организма ти. Тези процеси захранват твоя BHI резултат и препоръките за превенция.
              Тук виждаш само факторите ({total} на брой) върху които можеш реално да въздействаш.
              {autoCount > 0 && (
                <> {" "}<b className="text-sky-500">{autoCount}</b> от тях са попълнени автоматично от твоя здравен профил — с <span className="text-sky-500 font-medium">синьо</span> ниво. Можеш да ги приемеш или замениш.</>
              )}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${chipCls}`}>
                Попълнени: <b>{filledCount}</b> / {total}
              </span>
              <Button
                size="sm"
                variant="default"
                disabled={!dirty || saving}
                onClick={save}
                data-testid="prev-path-save"
                className="bg-emerald-500 hover:bg-emerald-600 text-white disabled:bg-slate-200 disabled:text-slate-600 dark:disabled:bg-slate-700 dark:disabled:text-slate-300"
              >
                {saving
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  : <Save className="w-3.5 h-3.5 mr-1.5" />}
                Запази избора
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={!dirty || saving}
                onClick={reset}
                data-testid="prev-path-reset"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                Отказ
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Category groups */}
      {groups.map((g) => {
        const isOpen = !!openCats[g.category];
        const filledInCat = g.factors.filter((f) => selections[f.id] || f.auto_populated).length;
        return (
          <div
            key={g.category}
            className={`${cardCls} border rounded-xl overflow-hidden`}
            data-testid={`prev-path-group-${g.category}`}
          >
            <button
              type="button"
              onClick={() => setOpenCats((s) => ({ ...s, [g.category]: !s[g.category] }))}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-black/[0.03] dark:hover:bg-white/[0.03] transition"
              data-testid={`prev-path-group-${g.category}-toggle`}
            >
              <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${accentFor(g.category)}`}>
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm md:text-base">{g.label_bg}</div>
                <div className={`text-xs ${mutedCls}`}>
                  {filledInCat} от {g.factors.length} попълнени
                </div>
              </div>
              {isOpen
                ? <ChevronUp className="w-4 h-4 shrink-0" />
                : <ChevronDown className="w-4 h-4 shrink-0" />}
            </button>

            {isOpen && (
              <div className="px-3 md:px-4 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                {g.factors.map((f) => {
                  const chosen = selections[f.id] || "";
                  const autoLevel = f.auto_populated;
                  const effective = chosen || autoLevel || "";
                  const IconComp = ICON_MAP[f.icon] || Sparkles;
                  const cardStyle = effective
                    ? "border-emerald-500/60 ring-1 ring-emerald-500/30"
                    : isDark ? "border-slate-700" : "border-slate-200";
                  return (
                    <div
                      key={f.id}
                      className={`rounded-lg border p-3 flex flex-col gap-2 ${cardStyle} ${
                        isDark ? "bg-slate-900/40" : "bg-slate-50/60"
                      }`}
                      data-testid={`prev-factor-${f.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${accentFor(f.category)}`}>
                          <IconComp className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold leading-tight break-words">
                            {f.name_bg}
                          </div>
                          {f.tooltip_bg && (
                            <div className={`text-[11px] mt-0.5 leading-snug ${mutedCls}`}>
                              {f.tooltip_bg}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => openEvidence(f)}
                          className="w-7 h-7 rounded-md border border-slate-300/40 flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/5 shrink-0"
                          title="Клинични доказателства"
                          data-testid={`prev-factor-${f.id}-info`}
                        >
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      <Select
                        value={effective || "__none__"}
                        onValueChange={(v) => setLevel(f.id, v === "__none__" ? "" : v)}
                      >
                        <SelectTrigger
                          className="h-8 text-xs"
                          data-testid={`prev-factor-${f.id}-select`}
                        >
                          <SelectValue placeholder="Изберете ниво…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Няма избор —</SelectItem>
                          {f.levels.map((lv) => (
                            <SelectItem
                              key={lv.key}
                              value={lv.key}
                              data-testid={`prev-factor-${f.id}-level-${lv.key}`}
                            >
                              {lv.label_bg}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {autoLevel && !chosen && (
                        <div
                          className="flex items-start gap-1.5 text-[11px] leading-snug text-sky-500 font-medium"
                          data-testid={`prev-factor-${f.id}-auto`}
                        >
                          <Wand2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          <span>
                            Автоматично от твоя профил — {f.auto_source_bg}. Смени го, ако не е точно.
                          </span>
                        </div>
                      )}
                      {chosen && (
                        <div className={`flex items-center gap-1.5 text-[11px] font-medium ${
                          initialSelections[f.id] === chosen ? "text-emerald-500" : "text-sky-500"
                        }`}>
                          <Check className="w-3.5 h-3.5" />
                          {initialSelections[f.id] === chosen ? "Запазено" : "Избрано (незапазено)"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Evidence modal */}
      <Dialog open={!!evidenceFor} onOpenChange={(o) => { if (!o) { setEvidenceFor(null); setEvidence(null); } }}>
        <DialogContent
          className={`max-w-2xl max-h-[85vh] overflow-y-auto ${
            isDark
              ? "bg-slate-900 border-slate-700 text-slate-100"
              : "bg-white border-slate-200 text-slate-900"
          }`}
          data-testid="evidence-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-emerald-500" />
              {evidenceFor?.name_bg}
            </DialogTitle>
            <DialogDescription>
              Кой тип рак повишава, при кои пациенти, и клинични обяснения.
            </DialogDescription>
          </DialogHeader>

          {evidenceLoading && (
            <div className="flex items-center gap-2 py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Зареждане…</span>
            </div>
          )}

          {evidence && (
            <div className="space-y-4 text-sm">
              {/* iter987 — Patient-first: which cancers this factor raises */}
              {evidence.cancer_types?.length > 0 && (
                <div className={`rounded-lg border p-3 ${
                  isDark ? "border-rose-500/30 bg-rose-500/5" : "border-rose-500/40 bg-rose-50"
                }`}>
                  <div className={`text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5 mb-2 ${
                    isDark ? "text-rose-300" : "text-rose-700"
                  }`}>
                    <BookOpen className="w-3.5 h-3.5" />
                    Кой тип рак повишава
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {evidence.cancer_types.map((c) => {
                      const strong = (c.weight || 0) >= 0.8;
                      return (
                        <span
                          key={c.id}
                          className={`text-xs px-2 py-1 rounded-full border ${
                            strong
                              ? "bg-rose-500/15 border-rose-500/50 text-rose-700 dark:text-rose-300 font-medium"
                              : (isDark ? "bg-slate-800 border-slate-600 text-slate-200" : "bg-white border-slate-300 text-slate-700")
                          }`}
                          title={c.weight != null ? `Сила на връзката: ${c.weight}` : ""}
                        >
                          {c.label_bg}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* iter987 — Patient-first: which patient subgroups */}
              {evidence.patient_subgroups?.length > 0 && (
                <div className={`rounded-lg border p-3 ${
                  isDark ? "border-sky-500/30 bg-sky-500/5" : "border-sky-500/40 bg-sky-50"
                }`}>
                  <div className={`text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5 mb-2 ${
                    isDark ? "text-sky-300" : "text-sky-700"
                  }`}>
                    <Info className="w-3.5 h-3.5" />
                    При кои пациенти важи най-силно
                  </div>
                  <ul className="space-y-1.5">
                    {evidence.patient_subgroups.map((p) => (
                      <li key={p.key} className="flex items-start gap-2 text-xs">
                        <span className="w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0 mt-1.5" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{p.label_bg}</div>
                          {p.exposure && (
                            <div className={mutedCls}>Ниво: {p.exposure}</div>
                          )}
                          {p.detail && (
                            <div className={`font-mono ${mutedCls}`}>{p.detail}</div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {evidence.grade && (
                <div>
                  <div className={`text-xs font-semibold uppercase tracking-wide ${mutedCls}`}>
                    Ниво на доказателство
                  </div>
                  <div className="mt-1">{evidence.grade_bg || evidence.grade}</div>
                </div>
              )}

              {Array.isArray(evidence.references) && evidence.references.length > 0 && (
                <div>
                  <div className={`text-xs font-semibold uppercase tracking-wide ${mutedCls}`}>
                    Референции
                  </div>
                  <ul className="list-disc list-inside mt-1 space-y-0.5 text-xs">
                    {evidence.references.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}

              {evidence.relative_risk && Object.keys(evidence.relative_risk).length > 0 && (
                <div>
                  <div className={`text-xs font-semibold uppercase tracking-wide ${mutedCls}`}>
                    Относителен риск
                  </div>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {Object.entries(evidence.relative_risk).map(([k, v]) => (
                      <div key={k} className={`px-2 py-1.5 rounded ${chipCls} text-xs`}>
                        <div className="font-medium">{k.replace(/_/g, " ")}</div>
                        <div className={mutedCls}>
                          {typeof v === "object"
                            ? `RR ${v.RR ?? v.HR ?? "–"} · CI ${v.CI || "–"}`
                            : String(v)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {evidence.notes?.mechanisms?.length > 0 && (
                <div>
                  <div className={`text-xs font-semibold uppercase tracking-wide ${mutedCls}`}>
                    Механизми
                  </div>
                  <ul className="list-disc list-inside mt-1 space-y-0.5 text-xs">
                    {evidence.notes.mechanisms.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              )}

              {evidence.notes?.direct_findings?.length > 0 && (
                <div>
                  <div className={`text-xs font-semibold uppercase tracking-wide ${mutedCls}`}>
                    Ключови находки
                  </div>
                  <ul className="list-disc list-inside mt-1 space-y-0.5 text-xs">
                    {evidence.notes.direct_findings.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </div>
              )}

              {evidence.activated_pathways?.length > 0 && (
                <div>
                  <div className={`text-xs font-semibold uppercase tracking-wide ${mutedCls}`}>
                    Активирани биологични процеси
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {evidence.activated_pathways.map((p) => (
                      <span
                        key={p.pathway_id}
                        className={`text-[11px] px-2 py-0.5 rounded-full border ${chipCls}`}
                        title={`base_weight ${p.base_weight}`}
                      >
                        {p.pathway_id} · {p.base_weight}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {evidence.notes?.limitations?.length > 0 && (
                <div>
                  <div className={`text-xs font-semibold uppercase tracking-wide ${mutedCls}`}>
                    Ограничения
                  </div>
                  <ul className="list-disc list-inside mt-1 space-y-0.5 text-xs">
                    {evidence.notes.limitations.map((l, i) => <li key={i}>{l}</li>)}
                  </ul>
                </div>
              )}

              {evidence.notes?.integration_comment && (
                <div className={`text-xs italic ${mutedCls}`}>
                  {evidence.notes.integration_comment}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
